import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { interpolateNodes, UiMessagesProvider, type UiMessages } from '@cedarreachai/ui';
import { formatDateTime, formatMoney } from './format';

/**
 * Calls `t` for a sentence that will carry React nodes, keeping the catalogue
 * in charge of word order. Each node argument is passed as its own `{name}`
 * token and swapped back in by `interpolateNodes`, so a value never passes
 * through ICU formatting and cannot be mistaken for syntax.
 */
function richT(t: TFunction, key: string, nodes: Readonly<Record<string, ReactNode>>): ReactNode {
  const tokens = Object.fromEntries(Object.keys(nodes).map((name) => [name, `{${name}}`]));
  return interpolateNodes(t(key, tokens), nodes);
}

/** Builds the shared library's messages from the app catalogue (`ui.*`). */
export function buildUiMessages(t: TFunction, locale: string): UiMessages {
  return {
    lifecycleState: (state) => t(`ui.lifecycle.${state}`),
    riskClass: (risk) => t(`ui.risk.${risk}`),
    findingSeverity: (severity) => t(`ui.severity.${severity}`),
    jobStepStatus: (status) => t(`ui.jobStep.${status}`),
    jobStepWithStatus: (parts) => richT(t, 'ui.jobStep.withStatus', parts),
    jobProgress: t('ui.jobStep.progress'),
    channelName: (channel) => t(`ui.channel.${channel}`),
    channelPreview: (channel) => t(`ui.channelPreview.${channel}`),
    close: t('ui.close'),
    cancel: t('ui.cancel'),
    confirm: t('ui.confirm'),
    working: t('ui.working'),
    brandSwitcher: {
      switchBrand: t('ui.brandSwitcher.switchBrand'),
      selectBrand: t('ui.brandSwitcher.selectBrand'),
      noBrands: t('ui.brandSwitcher.noBrands'),
    },
    approvalDiff: {
      contentHash: t('ui.approvalDiff.contentHash'),
      destinationHash: t('ui.approvalDiff.destinationHash'),
      noDestination: t('ui.approvalDiff.noDestination'),
      newVersion: (label) => t('ui.approvalDiff.newVersion', { label }),
      diff: (before, after) => t('ui.approvalDiff.diff', { before, after }),
    },
    claims: {
      summary: (count) => t('ui.claims.summary', { count }),
      unsupported: t('ui.claims.unsupported'),
      partial: (count) => t('ui.claims.partial', { count }),
      supported: (count) => t('ui.claims.supported', { count }),
    },
    cost: {
      currency: t('ui.cost.currency'),
      totalCap: t('ui.cost.totalCap'),
      dailyCap: t('ui.cost.dailyCap'),
      committedSpend: t('ui.cost.committedSpend'),
      remaining: t('ui.cost.remaining'),
      activeDates: t('ui.cost.activeDates'),
      targeting: t('ui.cost.targeting'),
      dateRange: (start, end) => t('ui.cost.dateRange', { start, end }),
      money: (amount, currency) => formatMoney(locale, amount, currency),
    },
    dangerous: {
      title: (action) => t('ui.dangerous.title', { action }),
      summary: (parts) => richT(t, 'ui.dangerous.summary', parts),
      scope: t('ui.dangerous.scope'),
      irreversible: t('ui.dangerous.irreversible'),
    },
    evidence: {
      title: t('ui.evidence.title'),
      description: t('ui.evidence.description'),
      empty: t('ui.evidence.empty'),
      loading: t('ui.evidence.loading'),
      errorRetriable: t('ui.evidence.errorRetriable'),
      errorTerminal: t('ui.evidence.errorTerminal'),
      retry: t('ui.evidence.retry'),
      source: t('ui.evidence.source'),
      trust: (state) => t('ui.evidence.trust', { state }),
      locator: t('ui.evidence.locator'),
      retrievedAt: t('ui.evidence.retrievedAt'),
      outcome: (outcome) => t('ui.evidence.outcome', { outcome }),
      formatTimestamp: (iso) => formatDateTime(locale, iso),
    },
    metric: {
      explain: (name) => t('ui.metric.explain', { name }),
      definition: (name) => t('ui.metric.definition', { name }),
    },
    pause: {
      headline: (workspace) => richT(t, 'ui.pause.headline', { workspace }),
      meta: ({ pausedBy, pausedAt, reason }) =>
        t('ui.pause.meta', {
          hasBy: pausedBy ? 'yes' : 'no',
          by: pausedBy ?? '',
          hasAt: pausedAt ? 'yes' : 'no',
          at: pausedAt ?? '',
          hasReason: reason ? 'yes' : 'no',
          reason: reason ?? '',
        }),
    },
    findings: {
      narrowBanner: (count) => t('ui.findings.narrowBanner', { count }),
      none: t('ui.findings.none'),
      suggestedFix: (fix) => t('ui.findings.suggestedFix', { fix }),
      notWaivable: t('ui.findings.notWaivable'),
      requestWaiver: t('ui.findings.requestWaiver'),
    },
  };
}

/**
 * Feeds `packages/ui` the current UI language. Rebuilt when the language
 * changes, so every shared component re-renders in the new locale at once.
 */
export function AppUiMessagesProvider({ children }: { readonly children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const messages = useMemo(() => buildUiMessages(t, locale), [t, locale]);
  return <UiMessagesProvider messages={messages}>{children}</UiMessagesProvider>;
}
